import React from 'react';
import { graphql, Link } from 'gatsby';
import clsx from 'clsx';

import Layout from '../components/Layout';
import Card from '../components/shared/Card';
import { Category, ImageWithAlt, Position, Slug } from '../types';
import { CONSTRUCTION_CATEGORIES } from '../constants';
import { mapEdgesToNodes } from '../utils';
import SEO from '../components/SEO';

interface Props {
  pageContext?: {
    category?: string;
  };
  data: {
    allSanityProject: {
      edges: Array<{
        node: {
          _id: string;
          slug: Slug;
          image: ImageWithAlt;
          title: string;
          category: Category;
        };
      }>;
    };
  };
}

interface TagProps {
  active: boolean;
  label: string;
  value: string;
}

const filters = [
  { value: 'svi', label: 'Svi projekti' },
  ...CONSTRUCTION_CATEGORIES.map((category) => ({
    value: category.slug,
    label: category.label,
  })),
];

const Tag: React.FC<TagProps> = ({ active, value, label }) => (
  <Link
    aria-current={active ? 'page' : undefined}
    className={clsx([
      'text-md inline-block cursor-pointer rounded-lg px-4 py-1.5 font-semibold hover:text-blue-500 focus-visible:outline-none focus-visible:ring-2',
      active ? 'bg-blue-50 text-blue-600' : 'text-gray-500',
    ])}
    to={value === 'svi' ? '/projekti/' : `/projekti/${value}/`}
  >
    {label}
  </Link>
);

function filterProjects(allSanityProject, selectedFilter) {
  const projects = mapEdgesToNodes(allSanityProject);

  if (selectedFilter === 'svi') {
    return projects;
  }

  return projects.filter(
    (project) => project.category.name.toLowerCase() === selectedFilter,
  );
}

const Projects: React.FC<Props> = ({
  pageContext,
  data: { allSanityProject },
}) => {
  const selectedFilter = pageContext?.category || 'svi';
  const filteredProjects = filterProjects(allSanityProject, selectedFilter);

  return (
    <Layout>
      <SEO title="Referenc lista" />

      <section className="container mt-10 md:mt-16">
        <h1 className="text-3xl font-medium leading-normal tracking-tight text-gray-800 md:text-4xl lg:text-5xl">
          Projekti
        </h1>

        <p className="mt-4 max-w-4xl text-lg leading-normal text-gray-600">
          Pregled završenih i aktivnih projekata iz visokogradnje, niskogradnje
          i hidrogradnje. <br className="hidden lg:block" />
          Imate pitanje ili planirate projekt?{' '}
          <Link
            className="font-medium text-blue-600 hover:text-blue-500 hover:underline"
            to="/#kontakt"
          >
            Pozovite nas ili pošaljite upit
          </Link>
          .
        </p>
      </section>

      <section className="container mt-14 sm:mt-16">
        <nav
          aria-label="Filter projekata"
          className="space-y-2 sm:space-x-1 sm:space-y-0"
        >
          {filters.map((filter) => (
            <Tag
              active={selectedFilter === filter.value}
              key={filter.value}
              label={filter.label}
              value={filter.value}
            />
          ))}
        </nav>

        <div className="mt-10 grid gap-x-5 gap-y-10 sm:grid-cols-2 xl:grid-cols-3">
          {filteredProjects.map((node) => (
            <Card
              href={`/projekti/${node.slug.current}`}
              image={node.image}
              key={node._id}
              position={Position.landscape}
              title={<h2>{node.title}</h2>}
              type={node.category.name}
            />
          ))}
        </div>
      </section>
    </Layout>
  );
};

export const query = graphql`
  query AllProjects {
    allSanityProject(sort: { fields: metadata___startDate, order: DESC }) {
      edges {
        node {
          _id
          title
          slug {
            current
          }
          category {
            name
          }
          image {
            alt
            asset {
              _id
              altText
              fluid {
                ...GatsbySanityImageFluid_withWebp
              }
            }
          }
        }
      }
    }
  }
`;

export default Projects;
